import React, { useState, useEffect } from 'react';
import { Sequence, Deployment } from '../../types/types';
import { extractDateFromDeployment } from '../../utils/deployment';

interface ListViewProps {
  isPreviewVideosRetrieved?: boolean;
  checkedItems: Record<string, boolean>;
  setCheckedItems: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  filteredDeployments: Deployment[];
}

const ListView: React.FC<ListViewProps> = ({
  isPreviewVideosRetrieved,
  checkedItems,
  setCheckedItems,
  filteredDeployments,
}) => {
  const [sequences, setSequences] = useState<Sequence[]>([]);

  useEffect(() => {
    const deploymentsAsSequences: Sequence[] = filteredDeployments.map((fd) => {
      const tags = new Set<string>();

      fd.trajectories.forEach((traj) => {
        Object.entries(traj.conditions ?? {}).forEach(([cond, value]) => {
          if (value === true) {
            tags.add(cond);
          }
        });
      });

      return {
        date: extractDateFromDeployment(fd),
        tags: Array.from(tags),
      };
    });

    const uniqueSequences = Array.from(
      new Map(deploymentsAsSequences.map((s) => [s.date, s])).values()
    );

    uniqueSequences.sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    setSequences(uniqueSequences);
  }, [filteredDeployments]);

  const handleCheckboxChange = (date: string) => {
    setCheckedItems((prev) => ({
      ...prev,
      [date]: !prev[date],
    }));
  };

  if (!isPreviewVideosRetrieved) {
    return (
      <div className="flex items-center justify-center">
        <svg
          className="animate-spin h-8 w-8 text-gray-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8H4z"
          />
        </svg>
        <span className="ml-2 text-gray-600">Loading List</span>
      </div>
    );
  }

  if (
    (!filteredDeployments || filteredDeployments.length === 0) &&
    isPreviewVideosRetrieved
  ) {
    return <p className="text-center">No available data</p>;
  }

  return (
    <div className="p-6 dark:bg-darkbg bg-white">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between dark:text-white text-gray-800 text-lg mb-4">
          <div className="flex-1 text-center font-semibold">Sequences</div>
          <div className="flex-1 text-center font-semibold">Tags</div>
        </div>
        {sequences.map((sequence) => (
          <div
            key={sequence.date}
            className="flex justify-between items-center dark:bg-gray-800 bg-gray-200 p-4 rounded-lg mb-4 shadow-md"
          >
            <div className="flex items-center">
              <input
                type="checkbox"
                className="mr-4"
                style={{ accentColor: 'gray' }}
                checked={!!checkedItems[sequence.date]}
                onChange={() => handleCheckboxChange(sequence.date)}
              />
              <span className="dark:text-white text-gray-800 text-sm">
                {sequence.date}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {sequence.tags.length > 0 ? (
                sequence.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-1 rounded-full text-xs font-medium
                   bg-gray-300 text-gray-800
                   dark:bg-gray-700 dark:text-gray-200"
                  >
                    {tag}
                  </span>
                ))
              ) : (
                <span className="text-gray-500 dark:text-gray-400 text-sm">
                  -
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ListView;
